/**
 * Reusable skeleton loading primitives.
 *
 * Page-level loading uses <LoadingScreen /> (animated DBGo logo).
 * Component-level loading uses skeleton variants that mimic the layout
 * shape of the content they replace.
 */

// ─── Basic building blocks ──────────────────────────────────

export function SkeletonLine({ width, className }: { width?: string; className?: string }) {
  return (
    <div
      className={`h-3 rounded-sm bg-dbgo-panel animate-pulse ${className ?? ''}`}
      style={width ? { width } : undefined}
    />
  );
}

export function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div className={`rounded-lg bg-dbgo-panel animate-pulse ${className ?? ''}`} />
  );
}

// ─── Composed skeletons ─────────────────────────────────────

/** Mimics the sidebar tree layout: indent + icon placeholder + text line. */
export function SkeletonTreeRows({ count = 5 }: { count?: number }) {
  // Pre-compute stable widths so they don't change on every render.
  const widths = [72, 85, 65, 78, 90, 60, 88, 70, 82, 75];
  return (
    <div className="py-1 px-2 space-y-1">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 py-[3px]"
          style={{ paddingLeft: i % 3 === 0 ? 4 : 20 }}
        >
          <div className="w-4 h-4 rounded-sm bg-dbgo-panel animate-pulse flex-shrink-0" />
          <div
            className="h-3 rounded-sm bg-dbgo-panel animate-pulse"
            style={{ width: `${widths[i % widths.length]}%` }}
          />
        </div>
      ))}
    </div>
  );
}

/** Mimics the AG Grid: header row + data rows with cell placeholders. */
export function SkeletonTable({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  // Pre-compute stable opacities for each cell.
  const opacities = [0.6, 0.8, 0.55, 0.75, 0.9, 0.65, 0.85, 0.7, 0.5, 0.95];
  return (
    <div className="p-2 space-y-1">
      {/* Header */}
      <div className="flex gap-3 py-2 border-b border-subtle">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="h-3 rounded-sm bg-dbgo-panel animate-pulse flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 py-2">
          {Array.from({ length: cols }).map((_, c) => (
            <div
              key={c}
              className="h-3 rounded-sm bg-dbgo-panel animate-pulse flex-1"
              style={{ opacity: opacities[(r * cols + c) % opacities.length] }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Mimics the InfoView stat cards grid. */
export function SkeletonStatCards({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-dbgo-panel border rounded-lg p-3 space-y-2 animate-pulse">
          <div className="h-2 w-16 rounded-sm bg-dbgo-root" />
          <div className="h-4 w-20 rounded-sm bg-dbgo-root" />
        </div>
      ))}
    </div>
  );
}

/** Multiple text lines with varying widths (last line shorter). */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  const widths = ['100%', '92%', '85%', '96%', '70%'];
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded-sm bg-dbgo-panel animate-pulse"
          style={{ width: i === lines - 1 ? '60%' : widths[i % widths.length] }}
        />
      ))}
    </div>
  );
}

// ─── Page-level loading screen ──────────────────────────────

/** Full-screen loading with animated DBGo logo and bouncing dots. */
export function LoadingScreen() {
  return (
    <div className="h-screen flex items-center justify-center bg-dbgo-root">
      <div className="flex flex-col items-center gap-3">
        <span className="text-2xl font-bold text-text-primary tracking-tight animate-dbgo-pulse">
          DBGo
        </span>
        <div className="flex gap-1">
          <div
            className="w-1.5 h-1.5 rounded-full bg-accent-blue animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full bg-accent-blue animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full bg-accent-blue animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    </div>
  );
}
